import React, { useEffect, useState } from "react";
import { usePager } from "../components/Pagination";
import { api, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Plus, Pencil, Trash2, Upload, Download, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Dialog, Input } from "./Suppliers";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import useSubmitGuard from "../lib/useSubmitGuard";
import { fmtQty, fmtFixed } from "../lib/format";
import { fmtIST } from "../lib/time";

const EMPTY = { code: "", name: "", category: "", uom: "PCS", hsn_code: "", pack_size: 10, min_stock: 0, max_stock: 0, cost: 0, drawing_ref: "", supplier_ids: [], qc_applicable: true, opening_stock: 0, active: true };

export default function Materials() {
  const { busy: submitBusy, guard: submitGuard, reset: resetGuard } = useSubmitGuard();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [newCatPrefix, setNewCatPrefix] = useState("");
  const [importBusy, setImportBusy] = useState(false);
  // Live-Prod-Fix follow-up — Price Trail dialog (price_history per material).
  const [trailOf, setTrailOf] = useState(null);   // material row
  const [trailRows, setTrailRows] = useState(null); // null = loading

  const openTrail = async (m) => {
    setTrailOf(m); setTrailRows(null);
    try {
      const r = await api.get(`/price-history?material_id=${m.id}`);
      setTrailRows(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load price history");
      setTrailRows([]);
    }
  };

  const load = async () => {
    const [m, s, c] = await Promise.all([api.get("/materials"), api.get("/suppliers"), api.get("/material-categories")]);
    setRows(m.data); setSuppliers(s.data); setCategories(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const handleCategoryChange = async (val) => {
    if (val === "__new__") {
      setNewCatName("");
      setNewCatPrefix("");
      setNewCatOpen(true);
      return;
    }
    setForm((f) => ({ ...f, category: val }));
    // Feb-2026: auto-suggest material code when blank and category has a prefix.
    if (!editing && !(form.code || "").trim() && val) {
      try {
        const { data } = await api.get(`/material-categories/next-code?category=${encodeURIComponent(val)}`);
        if (data?.suggested_code) setForm((f) => ({ ...f, code: data.suggested_code }));
      } catch { /* ignore */ }
    }
  };

  const submitNewCategory = async () => {
    const name = (newCatName || "").trim();
    const prefix = (newCatPrefix || "").trim().toUpperCase();
    if (!name) { toast.error("Category name is required"); return; }
    if (prefix && !/^[A-Z0-9]{2,5}$/.test(prefix)) { toast.error("Prefix must be 2-5 letters/digits"); return; }
    try {
      const { data } = await api.post("/material-categories", { name, prefix });
      setCategories((prev) => [...prev, data]);
      setForm((f) => ({ ...f, category: data.name }));
      // Auto-fill code if category has prefix and code is blank
      if (data.prefix && !editing && !(form.code || "").trim()) {
        try {
          const r = await api.get(`/material-categories/next-code?category=${encodeURIComponent(data.name)}`);
          if (r.data?.suggested_code) setForm((f) => ({ ...f, code: r.data.suggested_code }));
        } catch { /* ignore */ }
      }
      toast.success(`Category "${data.name}" added${data.prefix ? ` (prefix ${data.prefix})` : ""}`);
      setNewCatOpen(false); setNewCatName(""); setNewCatPrefix("");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to add category"); }
  };

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/bulk-import/materials.xlsx", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([r.data]));
      a.download = "materials_template.xlsx";
      a.click();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Download failed"); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/bulk-import/materials", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const s = r.data;
      toast.success(`Imported: ${s.created} created, ${s.updated} updated, ${s.skipped} skipped`);
      if ((s.errors || []).length) toast.error(`Errors: ${s.errors.slice(0, 3).map((x) => `Row ${x.row}: ${x.error}`).join(" · ")}`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed"); }
    finally { setImportBusy(false); e.target.value = ""; }
  };

  const save = async () => {
    try {
      const payload = { ...form, pack_size: +form.pack_size, min_stock: +form.min_stock, max_stock: +form.max_stock, cost: +(form.cost || 0), opening_stock: +(form.opening_stock || 0) };
      await submitGuard(async (idemKey) => {
        if (editing) await api.put(`/materials/${editing}`, payload, { headers: { "Idempotency-Key": idemKey } });
        else await api.post("/materials", payload, { headers: { "Idempotency-Key": idemKey } });
      });
      setOpen(false);
      toast.success(editing ? "Material updated" : "Material created");
      resetGuard();
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Delete material ${m.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/materials/${m.id}`);
      toast.success("Material deleted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggleSupplier = (id) => {
    setForm((f) => ({ ...f, supplier_ids: f.supplier_ids.includes(id) ? f.supplier_ids.filter((x) => x !== id) : [...f.supplier_ids, id] }));
  };

  const supName = (id) => suppliers.find((s) => s.id === id)?.name || "—";
  const nav = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("materials", "create");
  const canEdit = hasPermission("materials", "edit");
  const canDelete = hasPermission("materials", "delete");
  // Toolbar search — filters code / name / category / HSN client-side.
  const [search, setSearch] = useState("");
  const filteredRows = search.trim()
    ? rows.filter((m) => {
        const t = search.trim().toLowerCase();
        return [m.code, m.name, m.category, m.hsn_code]
          .some((v) => (v || "").toLowerCase().includes(t));
      })
    : rows;
  // Wave-J pagination
  const { visibleRows: pagedRows, controls: pageControls } = usePager(filteredRows, 10);

  return (
    <div data-testid="materials-page">
      <PageHeader title="Materials" subtitle="Parts master — BOM-linked to suppliers"
        right={canCreate ? (
          <div className="flex gap-2">
            <button onClick={downloadTemplate} className="border border-[#1565C0] text-[#1565C0] text-xs px-3 py-2 rounded-sm flex items-center gap-1 uppercase tracking-wider font-bold" data-testid="m-download-template"><Download size={12}/> Template</button>
            <label className={`border border-[#2E7D32] text-[#2E7D32] text-xs px-3 py-2 rounded-sm flex items-center gap-1 uppercase tracking-wider font-bold cursor-pointer ${importBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="m-bulk-import-btn">
              <Upload size={12}/> {importBusy ? "Importing…" : "Bulk Import"}
              <input type="file" accept=".xlsx,.xls" onChange={importExcel} className="hidden"/>
            </label>
            <button onClick={() => { setForm(EMPTY); setEditing(null); setOpen(true); }} className="bg-[#1565C0] hover:bg-[#0D2B5E] text-white text-sm px-4 py-2 rounded-sm flex items-center gap-2 uppercase tracking-wider font-semibold" data-testid="new-material-btn"><Plus size={14}/> New Material</button>
          </div>
        ) : null}
      />
      <div className="p-8">
        <div className="mb-3 relative w-96 max-w-full">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="materials-search"
            placeholder="Search code, name, category, HSN…"
            className="w-full pl-3 pr-8 py-2 border border-[#EEEEEE] rounded-sm text-sm bg-white focus:border-[#1565C0] outline-none"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#0D2B5E]/40 hover:text-[#C62828] text-sm" data-testid="materials-search-clear">×</button>
          )}
        </div>
        <div className="bg-white border border-[#EEEEEE] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
              <tr>
                <th className="text-left px-4 py-2">Code</th>
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Category</th>
                <th className="text-left px-4 py-2">UOM</th>
                <th className="text-right px-4 py-2">Pack</th>
                <th className="text-right px-4 py-2">Min</th>
                <th className="text-right px-4 py-2">Max</th>
                <th className="text-left px-4 py-2">Suppliers</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((m, i) => (
                <tr key={m.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                  <td className="px-4 py-2 font-mono text-[#1565C0]">{m.code}</td>
                  <td className="px-4 py-2 font-medium">{m.name}</td>
                  <td className="px-4 py-2">{m.category}</td>
                  <td className="px-4 py-2 font-mono text-xs">{m.uom}</td>
                  <td className="px-4 py-2 font-mono text-right">{m.pack_size}</td>
                  <td className="px-4 py-2 font-mono text-right">{fmtQty(m.min_stock)}</td>
                  <td className="px-4 py-2 font-mono text-right">{fmtQty(m.max_stock)}</td>
                  <td className="px-4 py-2 text-xs">{(m.supplier_ids || []).map(supName).join(", ") || "—"}</td>
                  <td className="px-4 py-2 text-right space-x-2">
                    <button onClick={() => nav(`/materials/${m.id}/history`)} className="text-[#1565C0] text-[10px] uppercase tracking-widest font-bold" data-testid={`history-material-${m.code}`}>History</button>
                    <button onClick={() => openTrail(m)} className="text-[#6A1B9A] text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-0.5" data-testid={`price-trail-${m.code}`} title="Rate movement trail (price_history)"><TrendingUp size={11}/>Trail</button>
                    {canEdit && <button onClick={() => { setForm(m); setEditing(m.id); setOpen(true); }} className="text-[#1565C0]" data-testid={`edit-material-${m.code}`}><Pencil size={14}/></button>}
                    {canDelete && <button onClick={() => del(m)} className="text-[#C62828]" data-testid={`delete-material-${m.code}`}><Trash2 size={14}/></button>}
                  </td>
                </tr>
              ))}
              {filteredRows.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-[#0D2B5E]/50">{search ? `No materials match "${search}".` : "No materials yet."}</td></tr>}
            </tbody>
          </table>
          {pageControls}
        </div>
      </div>

      {open && (
        <Dialog title={editing ? "Edit Material" : "New Material"} onClose={() => setOpen(false)} wide>
          <div className="grid grid-cols-3 gap-4">
            <Input label="Code" placeholder="Auto-generated from category prefix if blank" value={form.code} onChange={(v) => setForm({ ...form, code: v })} testId="m-code"/>
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="m-name"/>
            <div>
              <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-1">Category</div>
              <select
                value={form.category || ""}
                onChange={(e) => handleCategoryChange(e.target.value)}
                data-testid="m-category-select"
                className="w-full border border-[#EEEEEE] px-3 py-2 text-sm rounded-sm bg-white">
                <option value="">— Select —</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                {form.category && !categories.some((c) => c.name === form.category) && (
                  <option value={form.category}>{form.category}</option>
                )}
                <option value="__new__" data-testid="m-category-new">+ Add new category…</option>
              </select>
            </div>
            <Input label="UOM" value={form.uom} onChange={(v) => setForm({ ...form, uom: v })}/>
            <Input label="HSN Code" value={form.hsn_code} onChange={(v) => setForm({ ...form, hsn_code: v })}/>
            <Input label="Drawing Ref" value={form.drawing_ref} onChange={(v) => setForm({ ...form, drawing_ref: v })}/>
            <Input label="Child Part Cost (₹)" type="number" value={form.cost} onChange={(v) => setForm({ ...form, cost: v })} testId="m-cost"/>
            <Input label="Pack Size" type="number" value={form.pack_size} onChange={(v) => setForm({ ...form, pack_size: v })}/>
            <Input label="Min Stock" type="number" value={form.min_stock} onChange={(v) => setForm({ ...form, min_stock: v })}/>
            <Input label="Max Stock" type="number" value={form.max_stock} onChange={(v) => setForm({ ...form, max_stock: v })}/>
            <Input label="Opening Stock" type="number" value={form.opening_stock} onChange={(v) => setForm({ ...form, opening_stock: v })}/>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={form.qc_applicable} onChange={(e) => setForm({ ...form, qc_applicable: e.target.checked })} data-testid="m-qc-applicable"/>
              <span className="text-sm">Quality Check Applicable</span>
            </label>
          </div>
          <div className="mt-4">
            <div className="text-[10px] tracking-widest uppercase font-bold text-[#0D2B5E]/60 mb-2">Approved Suppliers</div>
            <div className="grid grid-cols-3 gap-2">
              {suppliers.map((s) => (
                <label key={s.id} className={`border ${form.supplier_ids.includes(s.id) ? "border-[#1565C0] bg-[#1565C0]/5" : "border-[#EEEEEE]"} px-3 py-2 rounded-sm cursor-pointer text-sm flex items-center gap-2`}>
                  <input type="checkbox" checked={form.supplier_ids.includes(s.id)} onChange={() => toggleSupplier(s.id)} data-testid={`m-sup-${s.code}`}/>
                  <span>{s.name}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={save} disabled={submitBusy} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed" data-testid="save-material-btn">{submitBusy ? "Saving…" : "Save"}</button>
          </div>
        </Dialog>
      )}

      {trailOf && (
        <Dialog title={`Price Trail — ${trailOf.code} · ${trailOf.name}`} onClose={() => setTrailOf(null)} wide>
          {trailRows === null ? (
            <div className="p-6 text-center text-sm text-[#0D2B5E]/50">Loading…</div>
          ) : trailRows.length === 0 ? (
            <div className="p-6 text-center text-sm text-[#0D2B5E]/50" data-testid="price-trail-empty">
              No price history yet. Entries appear when a GRN is created for this material (rate auto-fetched or manually entered).
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {(() => {
                  const rates = trailRows.map((r) => Number(r.resolved_rate) || 0).filter((x) => x > 0);
                  const latest = trailRows[0];
                  return (
                    <>
                      <div className="border border-[#EEEEEE] rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">Latest rate</div>
                        <div className="text-lg font-mono font-bold text-[#0D2B5E]" data-testid="price-trail-latest">₹ {fmtFixed(latest?.resolved_rate, 2)}</div>
                        <div className="text-[10px] text-[#0D2B5E]/50">{latest?.created_at ? fmtIST(latest.created_at) : ""}</div>
                      </div>
                      <div className="border border-[#EEEEEE] rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">Min / Max seen</div>
                        <div className="text-lg font-mono font-bold text-[#0D2B5E]" data-testid="price-trail-minmax">
                          {rates.length ? `₹ ${fmtFixed(Math.min(...rates), 2)} / ₹ ${fmtFixed(Math.max(...rates), 2)}` : "—"}
                        </div>
                        <div className="text-[10px] text-[#0D2B5E]/50">across {trailRows.length} GRN line{trailRows.length === 1 ? "" : "s"}</div>
                      </div>
                      <div className="border border-[#EEEEEE] rounded-sm p-3">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#0D2B5E]/60">Material master cost</div>
                        <div className="text-lg font-mono font-bold text-[#0D2B5E]" data-testid="price-trail-master-cost">₹ {fmtFixed(trailOf.cost, 2)}</div>
                        <div className="text-[10px] text-[#0D2B5E]/50">current fallback rate</div>
                      </div>
                    </>
                  );
                })()}
              </div>
              <table className="w-full text-sm border border-[#EEEEEE]" data-testid="price-trail-table">
                <thead className="bg-[#F5F5F5] text-[10px] uppercase tracking-widest text-[#0D2B5E]/60">
                  <tr>
                    <th className="text-left px-3 py-2">When (IST)</th>
                    <th className="text-left px-3 py-2">GRN</th>
                    <th className="text-left px-3 py-2">PO</th>
                    <th className="text-left px-3 py-2">Source</th>
                    <th className="text-right px-3 py-2">Typed</th>
                    <th className="text-right px-3 py-2">Resolved</th>
                    <th className="text-left px-3 py-2">By</th>
                  </tr>
                </thead>
                <tbody>
                  {trailRows.map((r, i) => (
                    <tr key={r.id} className={i % 2 === 0 ? "bg-white" : "bg-[#F5F5F5]/40"}>
                      <td className="px-3 py-1.5 text-xs font-mono">{fmtIST(r.created_at)}</td>
                      <td className="px-3 py-1.5 text-xs font-mono text-[#1565C0]">{r.grn_number || "—"}</td>
                      <td className="px-3 py-1.5 text-xs font-mono">{r.po_number || "—"}</td>
                      <td className="px-3 py-1.5">
                        <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm ${
                          r.source === "po" ? "bg-[#1565C0]/10 text-[#1565C0]"
                          : r.source === "gate_entry" ? "bg-[#00897B]/10 text-[#00897B]"
                          : r.source === "material" ? "bg-[#E65100]/10 text-[#E65100]"
                          : "bg-[#0D2B5E]/10 text-[#0D2B5E]"
                        }`} data-testid={`price-trail-src-${i}`}>{(r.source || "manual").replace("_", " ")}</span>
                      </td>
                      <td className="px-3 py-1.5 text-right text-xs font-mono">{r.previous_rate ? `₹ ${fmtFixed(r.previous_rate, 2)}` : "—"}</td>
                      <td className="px-3 py-1.5 text-right text-xs font-mono font-bold">₹ {fmtFixed(r.resolved_rate, 2)}</td>
                      <td className="px-3 py-1.5 text-xs">{r.user_name || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </Dialog>
      )}

      {newCatOpen && (
        <Dialog title="Add new material category" onClose={() => setNewCatOpen(false)}>  <Input label="Category name *" value={newCatName} onChange={setNewCatName} testId="new-cat-name"/>
          <div className="mt-3">
            <Input label="Prefix (optional, 2-5 letters — used for auto-generated material codes, e.g. RM, CNS)" value={newCatPrefix} onChange={(v) => setNewCatPrefix((v || "").toUpperCase())} testId="new-cat-prefix"/>
            <div className="text-[10px] text-[#0D2B5E]/50 mt-1">Cannot be changed after creation. Examples: RM → RM001, RM002… &nbsp; CNS → CNS001…</div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setNewCatOpen(false)} className="px-4 py-2 border border-[#EEEEEE] text-sm rounded-sm">Cancel</button>
            <button onClick={submitNewCategory} className="px-4 py-2 bg-[#1565C0] text-white text-sm rounded-sm font-semibold tracking-wider uppercase" data-testid="new-cat-submit">Add</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}
